import { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Spinner } from '~/components/atomic';
import { checkEvaluation, deleteEvaluateChat, fetchEvaluateChat } from '~/api';
import {
  ChatEvaluationRequest,
  DeleteEvaluateChatRequest,
  FetchEvaluateChatRequest,
  MessageResponse,
} from '~/types';
import { getMemberName, parseTextWithItalics, validateEmail } from '~/utils';
import AIMark from '~/assets/img/icons/chat_ai_mark.svg';
import { useCommonChatStore, useUserStore } from '~/store';
import useToast from '~/store/useToast';
import * as S from './ChatContent.styled';

interface ChatProps {
  chatroom_id?: string;
  type: 'group' | 'single';
  id: string;
  msg: string;
  send_time: string;
  name: string | undefined;
  isGrouped: boolean;
  showEvaluation: boolean;
  toggleEvaluation: () => void;
  isLastOrDifferentUser: boolean;
  isPending?: boolean;
  messages: MessageResponse[];
}

export default function Chat({
  type,
  id,
  msg,
  send_time,
  name,
  isGrouped,
  showEvaluation,
  toggleEvaluation,
  isLastOrDifferentUser,
  messages,
  isPending,
}: ChatProps) {
  const { id: chatroom_id } = useParams();
  const email = useUserStore((state) => state.email);
  const setSaveLastChatEvaluation = useCommonChatStore(
    (state) => state.setSaveLastChatEvaluation,
  );
  const members = useCommonChatStore((state) => state.chatMembers);
  const {
    id: lastMsgSend_id,
    send_time: lastMsgSend_time,
    like: lastMsgLike,
  } = useCommonChatStore((state) => ({
    id: state.id,
    send_time: state.send_time,
    like: state.like,
  }));

  const { showToast } = useToast();

  const [like, setLike] = useState<boolean | null>(null);
  const [evaluationChecked, setEvaluationChecked] = useState(false);

  const lastMessages = messages[messages.length - 1];

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];

    if (lastMessage.id === id && lastMessage.send_time === send_time) {
      setLike(lastMsgLike);
      setEvaluationChecked(true);
    } else {
      setLike(null); // 메시지가 달라질 경우 초기화
    }
  }, [lastMsgSend_id, lastMsgSend_time, lastMsgLike]);

  const userMsg = id === email;
  const aiMsg = !validateEmail(id);

  const renderTextSegments = (text: string) => {
    const segments = parseTextWithItalics(text);
    return segments.map(({ italic, text }, index) =>
      text === '\n' ? (
        <br key={index} />
      ) : (
        <p className={italic ? 'italic' : ''} key={index}>
          {text}
        </p>
      ),
    );
  };

  const handleToggleEvaluation = async () => {
    if (!chatroom_id) return;
    if (!evaluationChecked) {
      const requestData: ChatEvaluationRequest = {
        send_time,
        chatroom_id,
      };
      try {
        const { data } = await checkEvaluation(requestData);
        setLike(data.like);
        setEvaluationChecked(true);
      } catch (error) {
        console.error(error);
      }
    }

    toggleEvaluation();
  };

  const handleEvaluateChatLike = async (send_time: string, like: boolean) => {
    if (!chatroom_id) return;
    const requestData: FetchEvaluateChatRequest = {
      chatroom_id,
      send_time: `${send_time}`,
      like: like,
    };
    try {
      const { status } = await fetchEvaluateChat(requestData);
      if (status === 200) {
        setLike(like);
        if (lastMessages.id === id && lastMessages.send_time === send_time) {
          setSaveLastChatEvaluation({
            id,
            send_time,
            like,
          });
        }
        showToast('success', 'Thanks for your feedback!');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleEvaluateChatDelete = async (send_time: string) => {
    if (!chatroom_id) return;
    const requestData: DeleteEvaluateChatRequest = {
      chatroom_id,
      send_time,
    };
    try {
      const { status } = await deleteEvaluateChat(requestData);
      if (status === 200) {
        setLike(null);
        if (lastMessages.id === id && lastMessages.send_time === send_time) {
          setSaveLastChatEvaluation({
            id,
            send_time,
            like: null,
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Fragment>
      <S.ChatList isGrouped={isGrouped}>
        {isGrouped && !userMsg && (
          <S.ChatNameBox>
            {type === 'group' && getMemberName(members, id) && aiMsg && (
              <img src={AIMark} alt='group_icon' />
            )}
            <S.ChatName className='ai_name'>
              {!userMsg &&
                (name || getMemberName(members, id) || (
                  <span>Deleted character</span>
                ))}
            </S.ChatName>
          </S.ChatNameBox>
        )}

        <S.ChatContentWrapper className={userMsg ? 'user' : 'other'}>
          <S.Content
            className={`content ${isGrouped ? 'first' : ''}`}
            onClick={handleToggleEvaluation}
          >
            {renderTextSegments(msg)}
          </S.Content>
          {isLastOrDifferentUser && isPending && (
            <S.ChatTime>
              <Spinner size='small' color='content_light_5' />
            </S.ChatTime>
          )}
        </S.ChatContentWrapper>

        {showEvaluation && aiMsg && (
          <S.ChatEvaluation>
            <button
              title='chat like'
              onClick={() => {
                if (like === true) {
                  handleEvaluateChatDelete(send_time);
                } else {
                  handleEvaluateChatLike(send_time, true);
                }
              }}
            >
              <S.EvaluationButton>
                <S.EvaluationLikeIcon isLiked={like} size={16} />
              </S.EvaluationButton>
            </button>
            <button
              title='chat delete'
              onClick={() => {
                if (like === false) {
                  handleEvaluateChatDelete(send_time);
                } else {
                  handleEvaluateChatLike(send_time, false);
                }
              }}
            >
              <S.EvaluationButton>
                <S.EvaluationDisLikeIcon isLiked={like} size={16} />
              </S.EvaluationButton>
            </button>
          </S.ChatEvaluation>
        )}
      </S.ChatList>
    </Fragment>
  );
}
